import { Button } from '@creditornot/cb-button';
import { Spinner } from '@creditornot/cb-progress';
import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { useTelemetryViewEvent } from 'common/hooks';
import { ssmoContractReadyView } from 'core/telemetry/avo/Avo';
import PenAndPaper from 'modules/self-serve-sign-up/images/pen-and-paper.png';

import { getUserContractDetails, handleSsmoContractSigned } from '../../../apis/merchant-onboarding-service/api';
import { Typography as T } from '../../../common/typography';
import { ssmoContractReadyInteractionClickSignOrLater } from '../../../core/telemetry/avo/Avo';
import { authenticator } from '../../wolt-auth';
import StepPageWrapper from '../components/StepPageWrapper';
import { BASE_STEPS_PREVIEW_INTL_ID } from '../components/StepsPreview/constants';
import { SsmoStepsIdentifiers, SsmoTask } from '../constants';

type Props = {
  merchantId: string;
};

const PageHeading = styled(T.Heading3)`
  margin-block-end: 32px;
`;

const ContentHeading = styled(T.Title2)`
  font-weight: 700;
`;

const SignYourContract = styled.div`
  padding-block-end: 16px;
`;

const Body = styled(T.Body3)``;

const StyledImage = styled.img`
  margin-block-start: 130px;
  width: 200px;
  height: 200px;
`;

const MainContent = styled.div`
  padding-block: 56px;
`;

const StyledButton = styled(Button).attrs({
  size: 'large',
  variant: 'primary',
})`
  width: 100%;
`;

const StyledLaterButton = styled(Button).attrs({
  size: 'large',
  variant: 'secondary',
})`
  margin-block-start: 8px;
  width: 100%;
`;

const ContentSection = (props: { heading: string; body: string }) => {
  return (
    <>
      <ContentHeading>{props.heading}</ContentHeading>
      <div>
        <Body>{props.body}</Body>
      </div>
    </>
  );
};

/**
 * TODO:
 * - Check if contract is signed, if not then the button should go to the contract link
 * - If contract is signed then immediately hit the status-signed endpoint (which creates the merchant). On success: redirect to "Contract is signed" page
 *
 */

const SignContractPage = ({ merchantId }: Props) => {
  const history = useHistory();
  const { formatMessage } = useIntl();

  useTelemetryViewEvent('ssmo_contract_ready_view', {}, durationMs => ssmoContractReadyView({ durationMs }));

  const [tokenIsRefreshing, setTokenIsRefreshing] = useState(false);

  const { data: contractStatus, isFetching: isContractStatusFetching } = useQuery(
    ['/merchant-admin/merchants/{merchantId}/contract/signed-status', merchantId],
    () => {
      if (merchantId) {
        return handleSsmoContractSigned(merchantId);
      }
      return;
    },
    { enabled: !!merchantId },
  );

  const isContractSignedByMerchant = contractStatus?.isContractSignedByMerchant;

  const { data: contracts, isFetching: isContractDetailsLoading } = useQuery(['merchant-admin/me/contracts'], () =>
    getUserContractDetails(),
  );

  if (!contractStatus || isContractStatusFetching) {
    return (
      <StepPageWrapper
        title={formatMessage({ id: `${BASE_STEPS_PREVIEW_INTL_ID}.build-partnership.tasks.create-agreement.title` })}
        step={SsmoStepsIdentifiers.SignContract}
        task={SsmoTask.CONTRACT_SIGNING}
      >
        <Spinner size="large" />
      </StepPageWrapper>
    );
  }

  const contractUrl = contracts?.[0]?.signingUrl;

  return (
    <StepPageWrapper
      title={formatMessage({ id: `${BASE_STEPS_PREVIEW_INTL_ID}.build-partnership.tasks.create-agreement.title` })}
      step={SsmoStepsIdentifiers.SignContract}
      task={SsmoTask.CONTRACT_SIGNING}
    >
      <StyledImage src={PenAndPaper} />
      {isContractSignedByMerchant ? (
        <>
          <MainContent>
            <PageHeading>{formatMessage({ id: 'self-serve-sign-up.contract.contract-signed' })}</PageHeading>
            <Body>{formatMessage({ id: 'self-serve-sign-up.contract.contract-signed-elaboration' })}</Body>
          </MainContent>
          <StyledButton
            loading={tokenIsRefreshing}
            onClick={async () => {
              try {
                setTokenIsRefreshing(true);
                await authenticator.refreshUser();
                history.replace('/onboarding-page');
              } finally {
                setTokenIsRefreshing(false);
              }
            }}
          >
            <FormattedMessage id="state.done" />
          </StyledButton>
        </>
      ) : (
        <>
          <MainContent>
            <PageHeading>{formatMessage({ id: 'self-serve-sign-up.contract.contract-is-ready' })}</PageHeading>

            <SignYourContract>
              <ContentSection
                heading={formatMessage({ id: 'self-serve-sign-up.contract.sign-your-contract' })}
                body={formatMessage({ id: 'self-serve-sign-up.contract.contract-elaboration' })}
              />
            </SignYourContract>

            <div>
              <ContentSection
                heading={formatMessage({ id: 'self-serve-sign-up.contract.after-signing' })}
                body={formatMessage({ id: 'self-serve-sign-up.contract.after-signing-elaboration' })}
              />
            </div>
          </MainContent>

          <StyledButton
            loading={isContractDetailsLoading || !contractUrl}
            onClick={() => {
              ssmoContractReadyInteractionClickSignOrLater({
                clickTarget: 'ssmo_sign',
              });
              contractUrl && window.open(contractUrl, '_blank');
            }}
          >
            {formatMessage({ id: 'self-serve-sign-up.contract.sign-agreement' })}
          </StyledButton>

          <StyledLaterButton
            onClick={() => {
              ssmoContractReadyInteractionClickSignOrLater({
                clickTarget: 'ssmo_sign_later',
              });
              history.push('/onboarding-page');
            }}
          >
            {formatMessage({ id: 'self-serve-sign-up.contract.sign-later' })}
          </StyledLaterButton>
        </>
      )}
    </StepPageWrapper>
  );
};

export default SignContractPage;
